import { Injectable, inject } from '@angular/core';

import { getFromStorage, saveToStorageAsync } from '../helpers/storageUtils';
import {
  CotizacionPausaDescripciones,
  CotizacionPausaEstado,
} from '../Models/cotizacion-pausa-estado.enum';
import { NoHaySeguroDescripciones, NoHaySeguroEstado } from '../Models/no-hay-seguro-estado.enum';
import { YaTenemosDescripciones, YaTenemosEstado } from '../Models/ya-tenemos-datos-estado.enum';
import {
  YaTienesPolizaDescripciones,
  YaTienesPolizaEstado,
} from '../Models/ya-tienes-poliza-estado.enum';
import { DataLayerService } from './DataLayer/data-layer.service';

/**
 * Servicio centralizado para el manejo de eventos de tracking
 */
@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private dataLayerService = inject(DataLayerService);

  private codePause?: number;

  /**
   * Envía eventos de tracking para cotización en pausa
   * @param ecomItem - Tipo de evento: 'Pageview' o 'Button'
   * @param ecomSubitem - Subtipo opcional del evento
   * @param estadoEnum - Enum de estado (CotizacionPausaEstado, NoHaySeguroEstado, etc.)
   * @param descripcionesEnum - Enum de descripciones correspondiente
   */
  enviarEventoEstado(
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
    estadoEnum?: any,
    descripcionesEnum?: any,
    cotizacionPausa?: boolean,
  ): void {
    const estado = getFromStorage(pagina, 'session');
    console.log('esto es estado ', estado);
    const descripcion =
      descripcionesEnum && estadoEnum
        ? descripcionesEnum[estado as keyof typeof estadoEnum]
        : undefined;

    // this.logger.log(
    //     'Enviando evento de tracking - Estado:',
    //     estado,
    //     'Descripción:',
    //     descripcion,
    //     'Tipo:',
    //     ecomItem
    // );

    if (cotizacionPausa === true) {
      this.codePause = typeof estado === 'number' ? estado : Number(estado);
    }

    if (ecomSubitem) {
      this.dataLayerService.pushDataLayerStepErrorInit(
        ecomItem,
        estado?.toString(),
        pagina,
        //descripcion || `Estado - Código: ${estado}`,
        ecomSubitem,
      );
    } else {
      this.dataLayerService.pushDataLayerStepErrorInit(
        ecomItem,
        estado?.toString(),
        pagina,
        //descripcion || `Estado - Código: ${estado}`
      );
    }
  }

  /**
   * Envía eventos de tracking para cotización en pausa (compatibilidad)
   */
  enviarEventoCotizacionPausa(
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
  ): void {
    this.enviarEventoEstado(
      pagina,
      ecomItem,
      ecomSubitem,
      CotizacionPausaEstado,
      CotizacionPausaDescripciones,
      true,
    );
  }

  /**
   * Envía eventos de tracking para "No Hay Seguro"
   */
  enviarEventoNoHaySeguro(
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
  ): void {
    this.enviarEventoEstado(
      pagina,
      ecomItem,
      ecomSubitem,
      NoHaySeguroEstado,
      NoHaySeguroDescripciones,
    );
  }

  enviarEventoYaTenemosTusDatos(
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
  ): void {
    this.enviarEventoEstado(pagina, ecomItem, ecomSubitem, YaTenemosEstado, YaTenemosDescripciones);
  }

  enviarEventoYaTienesUnaPolizaActiva(
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
  ): void {
    this.enviarEventoEstado(
      pagina,
      ecomItem,
      ecomSubitem,
      YaTienesPolizaEstado,
      YaTienesPolizaDescripciones,
    );
  }

  /**
   * Guarda un estado en storage y luego envía el evento de tracking
   * Útil para asegurar que el valor esté disponible antes de navegar
   */
  async guardarEstadoYEnviarEvento(
    key: string,
    value: any,
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
    estadoEnum?: any,
    descripcionesEnum?: any,
    storage: 'local' | 'session' = 'session',
  ): Promise<void> {
    try {
      // Primero guardamos el estado
      await saveToStorageAsync(key, value, storage);

      // Luego enviamos el evento de tracking
      this.enviarEventoEstado(pagina, ecomItem, ecomSubitem, estadoEnum, descripcionesEnum);
    } catch (error) {
      console.error('Error al guardar estado y enviar evento:', error);
      // Aún así enviamos el evento para mantener el tracking
      this.enviarEventoEstado(pagina, ecomItem, ecomSubitem, estadoEnum, descripcionesEnum);
    }
  }

  /**
   * Guarda estado de cotización en pausa y envía evento
   */
  async guardarCotizacionPausaYEnviarEvento(
    key: string,
    value: any,
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
    storage: 'local' | 'session' = 'session',
  ): Promise<void> {
    return this.guardarEstadoYEnviarEvento(
      key,
      value,
      pagina,
      ecomItem,
      ecomSubitem,
      CotizacionPausaEstado,
      CotizacionPausaDescripciones,
      storage,
    );
  }

  /**
   * Guarda estado de "No Hay Seguro" y envía evento
   */
  async guardarNoHaySeguroYEnviarEvento(
    key: string,
    value: any,
    pagina: string,
    ecomItem: 'Pageview' | 'Button',
    ecomSubitem?: string,
    storage: 'local' | 'session' = 'session',
  ): Promise<void> {
    return this.guardarEstadoYEnviarEvento(
      key,
      value,
      pagina,
      ecomItem,
      ecomSubitem,
      NoHaySeguroEstado,
      NoHaySeguroDescripciones,
      storage,
    );
  }

  public getcodePause(): number {
    return this.codePause || -1;
  }
}
